const DEFAULT_PET_TYPE = 'dog';
const DEFAULT_MEAL_TYPE = 'enhanced';
const CAT_MEAL_TYPE = 'cat_single';
const DAYS_PER_WEEK = 7;
const MIN_MEAL_PACK_COUNT = 8;
const FREEZER_CAPACITY_EXTRA_PACKS = 2;
const AWTOMIC_BUNDLE_TYPE = 'dynamicBaB';
const MEAL_TYPES = {
  classic_raw: {
    label: 'Classic Raw',
    gramsPerPack: 450,
  },
  enhanced: {
    label: 'Enhanced',
    gramsPerPack: 450,
  },
  meatballs: {
    label: 'Meatballs',
    gramsPerPack: 360,
  },
  cat_single: {
    label: 'Cat food',
    gramsPerPack: 360,
  },
};
const MOCK_INTRO_VALUES = {
  petName: 'Jack',
  petBreed: 'Foxhound',
  petBreedUnknown: false,
};

const MOCK_QUESTIONNAIRE_VALUES = {
  pet: {
    sex: 'boy',
    neutered: 'no',
    ageYears: '2',
    ageMonths: '6',
    weightKg: '18',
    bodyShape: 'just_right',
    activity: 'active',
    hasAllergies: 'no',
    hasHealthConditions: 'no',
  },
  owner: {
    name: 'Test Customer',
    email: 'test@example.com',
    marketingConsent: false,
  },
};

function toNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function getAgeInMonths({ ageYears, ageMonths }) {
  return toNumber(ageYears) * 12 + toNumber(ageMonths);
}

function getDogFoodPercentageByAge(ageInMonths) {
  if (ageInMonths < 3) return 0.08;
  if (ageInMonths < 4) return 0.07;
  if (ageInMonths < 5) return 0.06;
  if (ageInMonths < 6) return 0.05;
  if (ageInMonths < 7) return 0.045;
  if (ageInMonths < 8) return 0.04;
  if (ageInMonths < 10) return 0.035;
  if (ageInMonths < 12) return 0.03;

  return 0.02;
}

function calculateDogNutrition(pet) {
  const weightKg = toNumber(pet.weightKg);
  const ageInMonths = getAgeInMonths(pet);
  const weightPercentageModifier = getDogFoodPercentageByAge(ageInMonths);

  return {
    ageInMonths,
    weightPercentageModifier,
    gramsPerDay: Math.round(weightKg * weightPercentageModifier * 1000),
  };
}

function getKittenFoodCoefficientByAge(ageInMonths) {
  // For food containing 136 kcal/100g.
  if (ageInMonths < 4) return 130;
  if (ageInMonths < 8) return 100;

  return 75;
}

function calculateCatNutrition(pet) {
  const weightKg = toNumber(pet.weightKg);
  const ageInMonths = getAgeInMonths(pet);
  // Age is entered in whole months, so 2 months is the first eligible option (8+ weeks).
  if (ageInMonths < 2) return null;

  const weightPercentageModifier = ageInMonths >= 12 ? 0.025 : null;
  const exactDailyGrams = ageInMonths < 12
    ? getKittenFoodCoefficientByAge(ageInMonths) * weightKg ** 0.75
    : weightKg * weightPercentageModifier * 1000;

  return {
    ageInMonths,
    weightPercentageModifier,
    exactDailyGrams,
    gramsPerDay: Math.round(exactDailyGrams),
  };
}

function calculatePetNutrition(pet) {
  if (pet.type === 'dog') {
    return calculateDogNutrition(pet);
  }

  if (pet.type === 'cat') {
    return calculateCatNutrition(pet);
  }

  return null;
}

function calculateDelivery({ gramsPerDay, packCount, gramsPerPack }) {
  if (!gramsPerDay || !packCount || !gramsPerPack) return null;

  const totalGrams = packCount * gramsPerPack;
  const packsPerDay = gramsPerDay / gramsPerPack;

  return {
    gramsPerDay,
    packCount,
    gramsPerPack,
    totalGrams,
    packsPerDay,
    frequencyDays: Math.floor(totalGrams / gramsPerDay),
  };
}

function roundNumber(value, precision = 2) {
  const multiplier = 10 ** precision;

  return Math.round(value * multiplier) / multiplier;
}

function getPackCountForDays({ gramsPerDay, gramsPerPack, days }) {
  if (!gramsPerDay || !gramsPerPack || !days) return 0;

  return Math.ceil((gramsPerDay * days) / gramsPerPack);
}

function calculateDeliveryAdjustment({ capacityPackCount, gramsPerDay, gramsPerPack, maxFrequencyWeeks = null }) {
  const safeCapacityPackCount = Math.max(MIN_MEAL_PACK_COUNT, toNumber(capacityPackCount, MIN_MEAL_PACK_COUNT));
  const maxAllowedPackCount = safeCapacityPackCount + FREEZER_CAPACITY_EXTRA_PACKS;
  const rawDelivery = calculateDelivery({
    gramsPerDay,
    packCount: safeCapacityPackCount,
    gramsPerPack,
  });

  if (!rawDelivery) {
    return {
      hasAdjustment: false,
      capacityPackCount: safeCapacityPackCount,
      packCount: safeCapacityPackCount,
      maxAllowedPackCount,
      freezerCapacityExtraPacks: FREEZER_CAPACITY_EXTRA_PACKS,
      reason: 'missing_delivery_inputs',
    };
  }

  const rawFrequencyDays = rawDelivery.totalGrams / gramsPerDay;
  const rawFrequencyWeeks = rawFrequencyDays / DAYS_PER_WEEK;
  let selectedWeeks = Math.max(1, Math.round(rawFrequencyWeeks));
  let ignoredCapacityTolerance = rawFrequencyDays < DAYS_PER_WEEK;
  let fallbackApplied = false;
  const maximumWeeks = maxFrequencyWeeks === null ? null : toNumber(maxFrequencyWeeks, null);

  if (ignoredCapacityTolerance) {
    selectedWeeks = 1;
  }

  if (maximumWeeks !== null && selectedWeeks > maximumWeeks) {
    selectedWeeks = maximumWeeks;
    fallbackApplied = true;
  }

  let adjustedPackCount = getPackCountForDays({
    gramsPerDay,
    gramsPerPack,
    days: selectedWeeks * DAYS_PER_WEEK,
  });

  // Weekly delivery rounding is intentionally conservative around freezer capacity.
  // If the nearest week interval needs more packs than the extra capacity allows,
  // prefer a shorter interval to avoid recommending a box the customer may not fit.
  while (!ignoredCapacityTolerance && selectedWeeks > 1 && adjustedPackCount > maxAllowedPackCount) {
    selectedWeeks -= 1;
    fallbackApplied = true;
    adjustedPackCount = getPackCountForDays({
      gramsPerDay,
      gramsPerPack,
      days: selectedWeeks * DAYS_PER_WEEK,
    });
  }

  while (adjustedPackCount < MIN_MEAL_PACK_COUNT) {
    const nextWeeks = selectedWeeks + 1;

    if (maximumWeeks !== null && nextWeeks > maximumWeeks) break;

    const nextPackCount = getPackCountForDays({
      gramsPerDay,
      gramsPerPack,
      days: nextWeeks * DAYS_PER_WEEK,
    });

    if (nextPackCount > maxAllowedPackCount) break;

    selectedWeeks = nextWeeks;
    adjustedPackCount = nextPackCount;
    fallbackApplied = true;
  }

  adjustedPackCount = Math.max(MIN_MEAL_PACK_COUNT, adjustedPackCount);

  const adjustedFrequencyDays = selectedWeeks * DAYS_PER_WEEK;
  const hasAdjustment = adjustedPackCount !== safeCapacityPackCount || adjustedFrequencyDays !== rawDelivery.frequencyDays;

  return {
    hasAdjustment,
    capacityPackCount: safeCapacityPackCount,
    packCount: adjustedPackCount,
    maxAllowedPackCount,
    freezerCapacityExtraPacks: FREEZER_CAPACITY_EXTRA_PACKS,
    rawPackCount: safeCapacityPackCount,
    rawFrequencyDays: roundNumber(rawFrequencyDays),
    rawFrequencyWeeks: roundNumber(rawFrequencyWeeks),
    selectedWeeks,
    adjustedFrequencyDays,
    adjustedPackCount,
    fallbackApplied,
    ignoredCapacityTolerance,
    capacityToleranceExceeded: adjustedPackCount > maxAllowedPackCount,
  };
}

function formatMoney(cents) {
  const moneyFormat = window.theme?.moneyFormatWithCodeForProductsPreference || window.theme?.moneyFormat || window.Shopify?.money_format;

  if (window.theme?.Shopify?.formatMoney) {
    return window.theme.Shopify.formatMoney(cents, moneyFormat);
  }

  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, moneyFormat);
  }

  const currency = window.Shopify?.currency?.active || 'GBP';

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function formatDailyPrice(cents) {
  const roundedCents = Math.round(cents);

  if (roundedCents < 100) {
    return `${roundedCents}p`;
  }

  return formatMoney(roundedCents);
}

function escapeHtml(value) {
  const element = document.createElement('div');
  element.textContent = value || '';

  return element.innerHTML;
}

function getShopifyGid(type, id) {
  return id ? `gid://shopify/${type}/${id}` : '';
}

function generateBundleUid() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `sq-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeWeeksLabel(weeks) {
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
}

function formatDeliveryInterval(weeks) {
  return weeks === 1 ? 'every week' : `every ${normalizeWeeksLabel(weeks)}`;
}

function getOptionValue(option) {
  if (typeof option === 'string') return option;

  return option?.value || option?.name || '';
}

function getSellingPlanWeeks(sellingPlan) {
  const optionValues = (sellingPlan?.options || []).map(getOptionValue);
  const labelValues = [sellingPlan?.name, ...optionValues];

  for (const value of labelValues) {
    const match = String(value || '')
      .toLowerCase()
      .match(/\b(\d+)\s*week/);

    if (match) {
      return toNumber(match[1], null);
    }
  }

  return null;
}

function getMealPlanByType(type) {
  const mealType = MEAL_TYPES[type] ? type : DEFAULT_MEAL_TYPE;

  return {
    type: mealType,
    ...MEAL_TYPES[mealType],
    products: [],
  };
}

function getDefaultMealTypeForPet(petType) {
  return petType === 'cat' ? CAT_MEAL_TYPE : DEFAULT_MEAL_TYPE;
}

function getProductVariant(product) {
  return product?.variants?.[0] || {};
}

function distributeProducts(products, packCount) {
  if (!products.length || !packCount) return [];

  const baseQuantity = Math.floor(packCount / products.length);
  const extraQuantityCount = packCount % products.length;

  return products
    .map((product, index) => {
      const variant = getProductVariant(product);

      return {
        productId: product.productId,
        variantId: variant.id || null,
        title: product.title,
        handle: product.handle,
        image: product.image,
        quantity: baseQuantity + (index < extraQuantityCount ? 1 : 0),
      };
    })
    .filter((product) => product.variantId && product.quantity > 0);
}

function getMockPetState(petType = DEFAULT_PET_TYPE) {
  const pet = {
    type: petType,
    name: MOCK_INTRO_VALUES.petName,
    breed: MOCK_INTRO_VALUES.petBreed,
    breedUnknown: MOCK_INTRO_VALUES.petBreedUnknown,
    ...MOCK_QUESTIONNAIRE_VALUES.pet,
  };

  return {
    ...pet,
    nutrition: calculatePetNutrition(pet),
  };
}

function getMockQuizState(petType = DEFAULT_PET_TYPE) {
  return {
    pet: getMockPetState(petType),
    owner: {
      ...MOCK_QUESTIONNAIRE_VALUES.owner,
    },
    box: {
      capacityPackCount: 8,
      packCount: 8,
    },
    mealPlan: getMealPlanByType(getDefaultMealTypeForPet(petType)),
    freeTreat: null,
    subscriptionTreats: [],
    delivery: null,
    deliveryAdjustment: null,
  };
}

class SubscriptionQuizStep extends HTMLElement {
  connectedCallback() {
    this.parentQuiz = this.closest('subscription-quiz');
  }

  submit() {
    this.increaseStep();
  }

  increaseStep() {
    this.parentQuiz?.increaseStep();
  }

  decreaseStep() {
    this.parentQuiz?.decreaseStep();
  }
}

class SubscriptionQuiz extends HTMLElement {
  currentStep = 0;
  state = getMockQuizState();

  connectedCallback() {
    this.petType = this.dataset.petType || DEFAULT_PET_TYPE;
    this.state = getMockQuizState(this.petType);
    const productsConfig = this.readProductsConfig();
    console.log('productsConfig', productsConfig);
    this.packPricesByMealType = productsConfig._packPrices || {};
    this.bundleProductConfig = productsConfig._bundleProduct || null;
    this.subscriptionTreatsConfig = productsConfig._subscriptionTreats || [];
    this.checkoutBehavior = productsConfig._checkoutBehavior || 'cart_drawer';
    this.productsByMealType = {
      ...productsConfig,
    };
    delete this.productsByMealType._packPrices;
    delete this.productsByMealType._bundleProduct;
    delete this.productsByMealType._subscriptionTreats;
    delete this.productsByMealType._checkoutBehavior;
    this.updateDeliveryPlan();
    this.steps = this.querySelectorAll('[data-quiz-step]');
    this.panels = this.querySelectorAll('[data-quiz-panel]');
    this.backButtons = this.querySelectorAll('[data-quiz-back]');
    this.progressFillElements = this.querySelectorAll('[data-quiz-progress-fill]');
    this.progressTextElements = this.querySelectorAll('[data-quiz-progress-text]');
    this.packFrequencyElements = this.querySelectorAll('[data-pack-frequency]');
    this.packWasPriceElements = this.querySelectorAll('[data-pack-price-was]');
    this.packNowPriceElements = this.querySelectorAll('[data-pack-price-now]');
    this.packPerDayPriceElements = this.querySelectorAll('[data-pack-price-per-day]');
    this.packDiscountLabelElements = this.querySelectorAll('[data-pack-discount-label]');
    this.mealTypeInputs = this.querySelectorAll('[data-meal-type-input]');
    this.mealTypePriceElements = this.querySelectorAll('[data-meal-type-price]');
    this.mealTypePricePerDayElements = this.querySelectorAll('[data-meal-type-price-per-day]');
    this.mealProductsContainer = this.querySelector('[data-meal-products]');
    this.mealTypeFooterElement = this.querySelector('[data-meal-type-footer]');
    this.mealTypeSubmitButton = this.querySelector('[data-meal-type-submit]');
    this.mealTypeCountElement = this.querySelector('[data-meal-type-count]');
    this.deliveryAdjustmentFeedbackElements = this.querySelectorAll('[data-delivery-adjustment-feedback]');
    this.freeTreatOptionElements = this.querySelectorAll('[data-free-treat-option]');
    this.subscriptionTreatsContainer = this.querySelector('.sq-subscription-treats__products');
    this.subscriptionTreatsSubmitButton = this.querySelector('[data-subscription-treats-submit]');

    this.backButtons.forEach((button) => button.addEventListener('click', this));
    this.mealProductsContainer?.addEventListener('click', this);
    this.mealTypeSubmitButton?.addEventListener('click', this);
    this.subscriptionTreatsContainer?.addEventListener('click', this);
    this.subscriptionTreatsSubmitButton?.addEventListener('click', this);
    document.addEventListener('subscription-quiz:intro-submit', this);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this);
    }

    window.subscriptionQuiz = this;
    this.render();
  }

  readProductsConfig() {
    const configElement = this.querySelector('[data-quiz-products]');
    if (!configElement) {
      return {};
    }

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      return {};
    }
  }

  disconnectedCallback() {
    this.backButtons.forEach((button) => button.removeEventListener('click', this));
    this.mealProductsContainer?.removeEventListener('click', this);
    this.mealTypeSubmitButton?.removeEventListener('click', this);
    this.subscriptionTreatsContainer?.removeEventListener('click', this);
    this.subscriptionTreatsSubmitButton?.removeEventListener('click', this);
    document.removeEventListener('subscription-quiz:intro-submit', this);

    if (window.Shopify?.designMode) {
      document.removeEventListener('shopify:block:select', this);
    }
  }

  handleEvent(event) {
    if (event.currentTarget?.matches?.('[data-quiz-back]')) {
      this.decreaseStep();
      return;
    }

    if (event.currentTarget === this.mealProductsContainer) {
      this.handleMealProductClick(event);
      return;
    }

    if (event.currentTarget === this.mealTypeSubmitButton) {
      this.submitMealType();
      return;
    }

    if (event.currentTarget === this.subscriptionTreatsContainer) {
      this.handleSubscriptionTreatClick(event);
      return;
    }

    if (event.currentTarget === this.subscriptionTreatsSubmitButton) {
      this.submitSubscriptionTreats();
      return;
    }

    if (event.type === 'shopify:block:select') {
      this.selectThemeEditorBlock(event);
      return;
    }

    if (event.type === 'subscription-quiz:intro-submit') {
      this.state.pet = {
        ...this.state.pet,
        type: this.petType,
        name: event.detail.petName,
        breed: event.detail.petBreed,
        breedUnknown: event.detail.petBreedUnknown,
      };

      console.log(this.state);

      this.increaseStep();
      return;
    }
  }

  submitQuestionnaire({ pet, owner }) {
    const nextPetState = {
      ...this.state.pet,
      ...pet,
      type: this.petType,
    };

    this.state.pet = {
      ...nextPetState,
    };
    this.state.pet.nutrition = calculatePetNutrition(this.state.pet);

    this.state.owner = owner;
    this.updateDeliveryPlan();

    console.log(this.state);

    this.increaseStep();
  }

  submitPacks(packCount) {
    this.state.box.capacityPackCount = toNumber(packCount);
    this.updateDeliveryPlan();

    console.log(this.state);

    this.increaseStep();
  }

  selectMealType(type) {
    this.state.mealPlan = getMealPlanByType(type);
    this.updateDeliveryPlan();

    console.log(this.state);

    this.render();
  }

  submitMealType() {
    if (!this.isMealPlanComplete()) return;

    console.log(this.state);

    this.increaseStep();
  }

  selectFreeTreat({ productId, variantId }) {
    if (this.state.freeTreat?.variantId === variantId) {
      this.state.freeTreat = null;
    } else {
      this.state.freeTreat = {
        productId,
        variantId,
      };
    }

    console.log(this.state);

    this.renderFreeTreat();
  }

  submitFreeTreat() {
    console.log(this.state);

    this.increaseStep();
  }

  async submitSubscriptionTreats() {
    if (this.isSubmittingSubscription) return;

    console.log('submit!', this.state);

    if (!this.isMealPlanComplete()) {
      alert('Please complete the meal plan before checkout.');
      return;
    }

    this.isSubmittingSubscription = true;
    this.setSubscriptionSubmitLoading(true);

    try {
      const payload = this.getAwtomicCartPayload();
      const cartDrawer = document.querySelector('cart-drawer');
      const shouldUpdateCartDrawer = this.checkoutBehavior === 'cart_drawer' && cartDrawer;

      console.log('Subscription quiz cart payload', payload);

      if (shouldUpdateCartDrawer) {
        payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cartDrawer.setActiveElement(this.subscriptionTreatsSubmitButton || document.activeElement);
      }

      const clearResponse = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!clearResponse.ok) {
        const error = await clearResponse.json().catch(() => null);

        throw new Error(error?.description || error?.message || 'Unable to clear the cart before checkout.');
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);

        throw new Error(error?.description || error?.message || 'Unable to add subscription box to cart.');
      }

      if (this.checkoutBehavior === 'checkout') {
        window.location.href = '/checkout';
        return;
      }

      if (shouldUpdateCartDrawer) {
        const cartData = await response.json();

        cartDrawer.classList.remove('is-empty');
        
        cartDrawer.renderContents(cartData);
        this.isSubmittingSubscription = false;
        this.setSubscriptionSubmitLoading(false);
        return;
      }

      window.location.href = '/cart';
    } catch (error) {
      console.error('Subscription quiz checkout error', error);
      alert(error.message || 'Unable to add subscription box to cart.');
      this.isSubmittingSubscription = false;
      this.setSubscriptionSubmitLoading(false);
    }
  }

  setSubscriptionSubmitLoading(isLoading) {
    if (!this.subscriptionTreatsSubmitButton) return;

    if (!this.subscriptionTreatsSubmitButton.dataset.defaultText) {
      this.subscriptionTreatsSubmitButton.dataset.defaultText = this.subscriptionTreatsSubmitButton.textContent.trim();
    }

    this.subscriptionTreatsSubmitButton.disabled = isLoading;
    this.subscriptionTreatsSubmitButton.classList.toggle('is-loading', isLoading);
    this.subscriptionTreatsSubmitButton.setAttribute('aria-busy', String(isLoading));
    this.subscriptionTreatsSubmitButton.textContent = isLoading
      ? 'Adding...'
      : this.subscriptionTreatsSubmitButton.dataset.defaultText;
  }

  calculateCurrentDelivery() {
    return calculateDelivery({
      gramsPerDay: this.state.pet.nutrition?.gramsPerDay,
      packCount: this.state.box.packCount,
      gramsPerPack: this.state.mealPlan.gramsPerPack,
    });
  }

  updateDeliveryPlan() {
    const bundleSellingPlanWeeks = this.getBundleSellingPlanWeeks();
    const adjustment = calculateDeliveryAdjustment({
      capacityPackCount: this.state.box.capacityPackCount,
      gramsPerDay: this.state.pet.nutrition?.gramsPerDay,
      gramsPerPack: this.state.mealPlan.gramsPerPack,
      maxFrequencyWeeks: bundleSellingPlanWeeks.length ? Math.max(...bundleSellingPlanWeeks) : null,
    });

    this.state.deliveryAdjustment = adjustment;
    this.state.box.packCount = adjustment.packCount;
    const delivery = this.calculateCurrentDelivery();

    this.state.delivery = delivery
      ? {
          ...delivery,
          foodLastsDays: delivery.frequencyDays,
          frequencyDays: adjustment.adjustedFrequencyDays || delivery.frequencyDays,
          frequencyWeeks: adjustment.selectedWeeks || null,
        }
      : null;
    this.state.mealPlan.products = this.getDefaultMealProducts();

    console.log('Subscription quiz delivery adjustment', adjustment);
  }

  getDefaultMealProducts() {
    const sourceProducts = this.productsByMealType[this.state.mealPlan.type] || [];
    const selectedProducts = distributeProducts(sourceProducts, this.state.box.packCount);

    console.log('Subscription quiz default meal products', {
      mealType: this.state.mealPlan.type,
      packCount: this.state.box.packCount,
      sourceProductCount: sourceProducts.length,
      sourceProducts,
      selectedProducts,
    });

    return selectedProducts;
  }

  selectThemeEditorBlock(event) {
    const block = event.target.closest('[data-shopify-editor-block]');
    const blockType = this.getThemeEditorBlockType(block);

    if (blockType === 'intro') {
      this.goToStep(0);
    }

    if (blockType === 'questionnaire') {
      this.goToStep(1);
    }

    if (blockType === 'packs') {
      this.goToStep(2);
    }

    if (blockType === 'meal_type') {
      this.goToStep(3);
    }

    if (blockType === 'single_meal_collection') {
      this.goToStep(3);
    }

    if (blockType === 'free_treat') {
      this.goToStep(4);
    }

    if (blockType === 'subscription_treats') {
      this.goToStep(5);
    }
  }

  getThemeEditorBlockType(block) {
    if (!block?.dataset.shopifyEditorBlock) return '';

    try {
      return JSON.parse(block.dataset.shopifyEditorBlock).type || '';
    } catch (error) {
      return '';
    }
  }

  goToStep(stepIndex) {
    this.currentStep = Math.min(Math.max(stepIndex, 0), this.steps.length - 1);
    this.render();
  }

  increaseStep() {
    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
    this.render();
    this.scrollToTop();
  }

  decreaseStep() {
    this.currentStep = Math.max(this.currentStep - 1, 0);
    this.render();
  }

  scrollToTop() {
    requestAnimationFrame(() => {
      const stickyHeaderHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height')) || 0;
      const top = this.getBoundingClientRect().top + window.scrollY - stickyHeaderHeight - 16;

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: 'smooth',
      });
    });
  }

  render() {
    this.renderPetName();
    this.renderPackFrequencies();
    this.renderPackPrices();
    this.renderMealType();
    this.renderMealTypePrices();
    this.renderMealTypePricePerDay();
    this.renderDeliveryAdjustmentFeedback();
    this.renderMealProducts();
    this.renderMealPlanStatus();
    this.renderFreeTreat();
    this.renderSubscriptionTreats();

    this.steps.forEach((step) => {
      step.hidden = Number(step.dataset.stepIndex) !== this.currentStep;
    });

    this.backButtons.forEach((button) => {
      button.disabled = this.currentStep === 0;
    });

    this.panels.forEach((panel) => {
      const firstVisibleStep = Number(panel.dataset.quizPanelFromStep || 1);

      panel.hidden = this.currentStep < firstVisibleStep;
    });

    const progress = this.calculateProgress();

    this.progressFillElements.forEach((element) => {
      element.style.width = `${progress}%`;
    });

    this.progressTextElements.forEach((element) => {
      element.textContent = progress;
    });
  }

  calculateProgress() {
    const lastStepIndex = this.steps.length - 1;

    if (lastStepIndex <= 0) return 0;

    return Math.round((this.currentStep / lastStepIndex) * 100);
  }

  renderPetName() {
    const petName = this.state.pet.name || 'Jack';

    this.querySelectorAll('[data-pet-name]').forEach((element) => {
      element.textContent = petName;
    });
  }

  renderPackFrequencies() {
    this.packFrequencyElements.forEach((element) => {
      const delivery = this.getPackDelivery(element.dataset.packCount);

      if (!delivery) return;

      element.textContent = delivery.frequencyDays;
    });
  }

  renderPackPrices() {
    const discountPercentage = this.getBundleDiscountPercentage();
    const discountMultiplier = (100 - discountPercentage) / 100;

    this.packDiscountLabelElements.forEach((element) => {
      element.textContent = `${discountPercentage}% OFF:`;
    });

    [8, 16, 24].forEach((packCount) => {
      const price = this.getPackPrice(this.state.mealPlan.type, packCount);

      if (price === null) return;

      const discountedPrice = Math.round(price * discountMultiplier);

      this.querySelectorAll(`[data-pack-price-was][data-pack-count="${packCount}"]`).forEach((element) => {
        element.textContent = `WAS: ${formatMoney(price)}`;
      });

      this.querySelectorAll(`[data-pack-price-now][data-pack-count="${packCount}"]`).forEach((element) => {
        element.textContent = formatMoney(discountedPrice);
      });

      this.querySelectorAll(`[data-pack-price-per-day][data-pack-count="${packCount}"]`).forEach((element) => {
        const frequencyDays = this.getPackFrequencyDays(packCount);

        if (!frequencyDays) return;

        element.textContent = formatMoney(Math.round(discountedPrice / frequencyDays));
      });
    });
  }

  renderMealTypePrices() {
    this.mealTypePriceElements.forEach((element) => {
      const mealType = element.dataset.mealType;
      const price = this.getMealTypeDailyPrice(mealType);

      if (price === null) return;

      element.textContent = formatDailyPrice(price);
    });
  }

  getCurrentMealPlanPrice() {
    const frequencyWeeks = toNumber(this.state.delivery?.frequencyWeeks, null);

    if (frequencyWeeks === null) return null;

    return this.state.mealPlan.products.reduce((total, product) => {
      if (total === null) return null;
      if (!product.quantity) return total;

      const variant = this.getConfiguredVariant(product.variantId);
      const allocation = this.getSellingPlanAllocationForVariant(variant, frequencyWeeks);
      const price = toNumber(allocation?.price, null);

      return price === null ? null : total + (price * product.quantity);
    }, 0);
  }

  renderMealTypePricePerDay() {
    const price = this.getCurrentMealPlanPrice();
    const frequencyDays = toNumber(this.state.delivery?.frequencyDays, null);

    if (price === null || frequencyDays === null || frequencyDays <= 0) return;

    const dailyPrice = Math.round(price / frequencyDays);

    this.mealTypePricePerDayElements.forEach((element) => {
      element.textContent = formatMoney(dailyPrice);
    });
  }

  renderDeliveryAdjustmentFeedback() {
    const adjustment = this.state.deliveryAdjustment;
    const shouldShow = Boolean(adjustment?.packCount && adjustment.selectedWeeks);
    const petName = this.state.pet.name || 'your pet';

    this.deliveryAdjustmentFeedbackElements.forEach((element) => {
      element.hidden = !shouldShow;

      if (!shouldShow) {
        element.textContent = '';
        return;
      }

      const packLabel = adjustment.packCount === 1 ? 'pack' : 'packs';
      const deliveryInterval = formatDeliveryInterval(adjustment.selectedWeeks);

      element.textContent = adjustment.hasAdjustment
        ? `For the best fit, we've adjusted ${petName}'s plan to ${adjustment.packCount} ${packLabel} delivered ${deliveryInterval}, based on your pet's details.`
        : `${adjustment.packCount} ${packLabel} delivered ${deliveryInterval} is the best fit for ${petName}.`;
    });
  }

  getMealTypeDailyPrice(mealType) {
    const discountMultiplier = (100 - this.getBundleDiscountPercentage()) / 100;
    const dailyPrices = [8, 16, 24]
      .map((packCount) => {
        const price = this.getPackPrice(mealType, packCount);
        const frequencyDays = this.getPackFrequencyDays(packCount, mealType);

        if (price === null || !frequencyDays) return null;

        return (price * discountMultiplier) / frequencyDays;
      })
      .filter((price) => price !== null);

    if (!dailyPrices.length) return null;

    return Math.min(...dailyPrices);
  }

  getBundleDiscountPercentage() {
    const firstPlanAdjustment = this.bundleProductConfig?.variant?.sellingPlanAllocations?.[0]?.sellingPlan?.priceAdjustments?.[0];

    // IMPORTANT: USE THE FIRST LINKED PLAN ADJUSTMENT; ALL CURRENT DELIVERY PLANS SHARE THE SAME PROMOTIONAL DISCOUNT.
    if (firstPlanAdjustment?.valueType !== 'percentage') return 0;

    return Math.max(0, toNumber(firstPlanAdjustment.value));
  }

  getPackDelivery(packCount, mealType = this.state.mealPlan.type) {
    const mealPlan = getMealPlanByType(mealType);

    return calculateDelivery({
      gramsPerDay: this.state.pet.nutrition?.gramsPerDay,
      packCount: toNumber(packCount),
      gramsPerPack: mealPlan.gramsPerPack,
    });
  }

  getPackFrequencyDays(packCount, mealType = this.state.mealPlan.type) {
    const delivery = this.getPackDelivery(packCount, mealType);

    if (delivery?.frequencyDays) {
      return delivery.frequencyDays;
    }

    const frequencyElement = this.querySelector(`[data-pack-frequency][data-pack-count="${packCount}"]`);

    return toNumber(frequencyElement?.textContent, 0);
  }

  getPackPrice(mealType, packCount) {
    const packPrice = this.packPricesByMealType?.[mealType]?.[packCount];
    const mappedPrice = toNumber(packPrice?.price, null);

    if (mappedPrice !== null) {
      return mappedPrice;
    }

    const product = this.productsByMealType[mealType]?.[0];
    const variant = product?.variants?.[0];
    const price = toNumber(variant?.price, null);

    return price === null ? null : price * toNumber(packCount);
  }

  renderMealType() {
    this.mealTypeInputs.forEach((input) => {
      input.checked = input.value === this.state.mealPlan.type;
    });
  }

  renderMealProducts() {
    if (!this.mealProductsContainer) return;

    const productsRenderKey = this.state.mealPlan.products.map((product) => `${product.productId}:${product.variantId}:${product.quantity}`).join('|');

    if (this.mealProductsRenderKey === productsRenderKey) {
      this.syncMealProductControls();
      return;
    }

    const shouldAnimate = this.mealProductsContainer.children.length > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const productsHtml = this.state.mealPlan.products.map((product) => this.getMealProductCardHtml(product)).join('');

    this.mealProductsRenderKey = productsRenderKey;

    if (shouldAnimate) {
      this.mealProductsContainer.classList.add('is-updating');
    }

    this.mealProductsContainer.innerHTML = productsHtml;
    this.syncMealProductControls();

    if (shouldAnimate) {
      requestAnimationFrame(() => {
        this.mealProductsContainer.classList.remove('is-updating');
      });
    }
  }

  getMealProductCardHtml(product) {
    const title = escapeHtml(product.title);

    return `
      <article class="sq-meal-product" data-product-id="${product.productId}">
        <img class="sq-meal-product__image" src="${escapeHtml(product.image)}" alt="${title}" loading="lazy">
        <h4 class="sq-meal-product__title">${title}</h4>
        <div class="sq-meal-product__actions">
          <button class="sq-meal-product__add" type="button" data-meal-product-action="increase" data-product-id="${product.productId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M11.332 6.002c1.45.008 2.235.072 2.748.585.585.586.585 1.529.585 3.415v.666c0 1.886 0 2.829-.585 3.415-.586.585-1.529.585-3.415.585H5.332c-1.886 0-2.829 0-3.414-.585-.586-.586-.586-1.529-.586-3.415v-.666c0-1.886 0-2.829.586-3.415.512-.513 1.297-.577 2.747-.585" stroke="#FFFBF4" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M8 1.334v8.667m2-2.334-2 2.334-2-2.334" stroke="#FFFBF4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>  
            Add to bundle
          </button>
          <div class="sq-meal-product__stepper" data-meal-product-stepper data-product-id="${product.productId}">
            <button class="sq-meal-product__remove" type="button" data-meal-product-action="remove" data-product-id="${product.productId}" aria-label="Remove ${title} and redistribute quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20.5001 6H3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M18.8346 8.5L18.3747 15.3991C18.1977 18.054 18.1092 19.3815 17.2442 20.1907C16.3792 21 15.0488 21 12.388 21H11.6146C8.95382 21 7.62342 21 6.75841 20.1907C5.8934 19.3815 5.8049 18.054 5.62791 15.3991L5.16797 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M9.5 11L10 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M14.5 11L14 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M6.5 6C6.55588 6 6.58382 6 6.60915 5.99936C7.43259 5.97849 8.15902 5.45491 8.43922 4.68032C8.44784 4.65649 8.45667 4.62999 8.47434 4.57697L8.57143 4.28571C8.65431 4.03708 8.69575 3.91276 8.75071 3.8072C8.97001 3.38607 9.37574 3.09364 9.84461 3.01877C9.96213 3 10.0932 3 10.3553 3H13.6447C13.9068 3 14.0379 3 14.1554 3.01877C14.6243 3.09364 15.03 3.38607 15.2493 3.8072C15.3043 3.91276 15.3457 4.03708 15.4286 4.28571L15.5257 4.57697C15.5433 4.62992 15.5522 4.65651 15.5608 4.68032C15.841 5.45491 16.5674 5.97849 17.3909 5.99936C17.4162 6 17.4441 6 17.5 6" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
            <div class="sq-meal-product__quantity">
              <button class="sq-meal-product__quantity-button" type="button" data-meal-product-action="decrease" data-product-id="${product.productId}" aria-label="Remove one ${title}">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M15 12H9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
              <span class="sq-meal-product__quantity-value" data-meal-product-quantity data-product-id="${product.productId}">${product.quantity}</span>
              <button class="sq-meal-product__quantity-button" type="button" data-meal-product-action="increase" data-product-id="${product.productId}" aria-label="Add one ${title}">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                  <path d="M15 12L12 12M12 12L9 12M12 12L12 9M12 12L12 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </article>
    `;
  }

  renderMealPlanStatus() {
    const selectedCount = this.getMealPlanProductTotal();
    const targetCount = this.state.box.packCount;
    const isComplete = selectedCount === targetCount;
    this.mealTypeFooterElement?.classList.toggle('valid', isComplete);
    this.mealTypeFooterElement?.classList.toggle('invalid', !isComplete);

    // if (this.mealTypeSubmitButton) {
    //   this.mealTypeSubmitButton.disabled = !isComplete;
    // }

    if (this.mealTypeCountElement) {
      this.mealTypeCountElement.textContent = `${selectedCount}/${targetCount}`;
    }

    this.renderMealTypePricePerDay();
  }

  renderFreeTreat() {
    this.freeTreatOptionElements.forEach((element) => {
      const isSelected = this.state.freeTreat?.variantId === element.dataset.variantId;

      element.classList.toggle('is-selected', isSelected);
      element.setAttribute('aria-pressed', String(isSelected));
    });
  }

  renderSubscriptionTreats() {
    this.syncSubscriptionTreatControls();
  }

  syncMealProductControls() {
    if (!this.mealProductsContainer) return;

    const selectedCount = this.getMealPlanProductTotal();
    const isFull = selectedCount >= this.state.box.packCount;
    const activeProductCount = this.state.mealPlan.products.filter((product) => product.quantity > 0).length;
    const canRemoveProduct = activeProductCount > 1;

    this.state.mealPlan.products.forEach((product) => {
      const quantityElement = this.mealProductsContainer.querySelector(`[data-meal-product-quantity][data-product-id="${product.productId}"]`);
      const decreaseButton = this.mealProductsContainer.querySelector(`[data-meal-product-action="decrease"][data-product-id="${product.productId}"]`);
      const increaseButtons = this.mealProductsContainer.querySelectorAll(`[data-meal-product-action="increase"][data-product-id="${product.productId}"]`);
      const removeButton = this.mealProductsContainer.querySelector(`[data-meal-product-action="remove"][data-product-id="${product.productId}"]`);
      const addButton = this.mealProductsContainer.querySelector(`.sq-meal-product__add[data-product-id="${product.productId}"]`);
      const stepperElement = this.mealProductsContainer.querySelector(`[data-meal-product-stepper][data-product-id="${product.productId}"]`);
      const isSelected = product.quantity > 0;

      if (quantityElement) {
        quantityElement.textContent = product.quantity;
      }

      if (addButton) {
        addButton.hidden = isSelected;
        addButton.disabled = isFull;
      }

      if (stepperElement) {
        stepperElement.hidden = !isSelected;
      }

      if (decreaseButton) {
        decreaseButton.disabled = product.quantity <= 0;
      }

      increaseButtons.forEach((button) => {
        button.disabled = isFull;
      });

      if (removeButton) {
        removeButton.disabled = product.quantity <= 0 || !canRemoveProduct;
      }
    });
  }

  handleMealProductClick(event) {
    const actionButton = event.target.closest('[data-meal-product-action]');

    if (!actionButton) return;

    const productId = toNumber(actionButton.dataset.productId, null);
    const action = actionButton.dataset.mealProductAction;

    if (productId === null) return;

    if (action === 'increase') {
      this.increaseMealProduct(productId);
    }

    if (action === 'decrease') {
      this.decreaseMealProduct(productId);
    }

    if (action === 'remove') {
      this.removeMealProduct(productId);
    }
  }

  increaseMealProduct(productId) {
    if (this.getMealPlanProductTotal() >= this.state.box.packCount) return;

    this.updateMealProductQuantity(productId, 1);
  }

  decreaseMealProduct(productId) {
    this.updateMealProductQuantity(productId, -1);
  }

  updateMealProductQuantity(productId, delta) {
    this.state.mealPlan.products = this.state.mealPlan.products.map((product) => {
      if (product.productId !== productId) return product;

      return {
        ...product,
        quantity: Math.max(product.quantity + delta, 0),
      };
    });

    this.syncMealProductControls();
    this.renderMealPlanStatus();
  }

  removeMealProduct(productId) {
    const removedProduct = this.state.mealPlan.products.find((product) => product.productId === productId);
    const remainingProducts = this.state.mealPlan.products.filter((product) => product.productId !== productId && product.quantity > 0);

    if (!removedProduct || removedProduct.quantity <= 0 || !remainingProducts.length) return;

    this.state.mealPlan.products = this.state.mealPlan.products.map((product) => {
      if (product.productId === productId) {
        return {
          ...product,
          quantity: 0,
        };
      }

      return product;
    });

    this.distributeQuantityAcrossProducts(remainingProducts, removedProduct.quantity).forEach((redistributedProduct) => {
      const product = this.state.mealPlan.products.find((item) => item.productId === redistributedProduct.productId);

      if (product) {
        product.quantity = redistributedProduct.quantity;
      }
    });

    this.syncMealProductControls();
    this.renderMealPlanStatus();
  }

  distributeQuantityAcrossProducts(products, quantity) {
    if (!products.length || !quantity) return products;

    const nextProducts = products.map((product) => ({ ...product }));

    for (let index = 0; index < quantity; index += 1) {
      nextProducts.sort((firstProduct, secondProduct) => firstProduct.quantity - secondProduct.quantity);
      nextProducts[0].quantity += 1;
    }

    return nextProducts;
  }

  getMealPlanProductTotal() {
    return this.state.mealPlan.products.reduce((total, product) => total + product.quantity, 0);
  }

  isMealPlanComplete() {
    const mealPackCount = this.getMealPlanProductTotal();

    return mealPackCount >= MIN_MEAL_PACK_COUNT && mealPackCount === this.state.box.packCount;
  }

  syncSubscriptionTreatControls() {
    if (!this.subscriptionTreatsContainer) return;

    this.subscriptionTreatsContainer.querySelectorAll('[data-subscription-treat-product]').forEach((productElement) => {
      const productId = toNumber(productElement.dataset.productId, null);
      const treat = this.state.subscriptionTreats.find((item) => item.productId === productId);
      const quantity = treat?.quantity || 0;
      const quantityElement = productElement.querySelector('[data-subscription-treat-quantity]');
      const addButton = productElement.querySelector('[data-subscription-treat-action="increase"].sq-subscription-treat__add');
      const quantityControls = productElement.querySelector('[data-subscription-treat-quantity-controls]');
      const decreaseButton = productElement.querySelector('[data-subscription-treat-action="decrease"]');
      const isSelected = quantity > 0;

      if (quantityElement) {
        quantityElement.textContent = quantity;
      }

      if (addButton) {
        addButton.hidden = isSelected;
      }

      if (quantityControls) {
        quantityControls.hidden = !isSelected;
      }

      if (decreaseButton) {
        decreaseButton.disabled = quantity <= 0;
      }
    });
  }

  handleSubscriptionTreatClick(event) {
    const actionButton = event.target.closest('[data-subscription-treat-action]');

    if (!actionButton) return;

    const productId = toNumber(actionButton.dataset.productId, null);
    const variantId = toNumber(actionButton.dataset.variantId, null);
    const action = actionButton.dataset.subscriptionTreatAction;

    if (productId === null || variantId === null) return;

    this.updateSubscriptionTreatQuantity({
      productId,
      variantId,
      delta: action === 'decrease' ? -1 : 1,
    });
  }

  updateSubscriptionTreatQuantity({ productId, variantId, delta }) {
    const currentTreat = this.state.subscriptionTreats.find((item) => item.productId === productId);
    const currentQuantity = currentTreat?.quantity || 0;
    const nextQuantity = Math.max(currentQuantity + delta, 0);
    const nextTreats = this.state.subscriptionTreats.filter((item) => item.productId !== productId);
    const treatConfig = this.getSubscriptionTreatConfig(variantId);

    if (nextQuantity > 0) {
      nextTreats.push({
        productId,
        variantId,
        title: treatConfig?.title || '',
        quantity: nextQuantity,
      });
    }

    this.state.subscriptionTreats = nextTreats;

    console.log(this.state);

    this.syncSubscriptionTreatControls();
  }

  getAwtomicCartPayload() {
    const bundleProduct = this.bundleProductConfig;
    const frequencyWeeks = toNumber(this.state.delivery?.frequencyWeeks, null);
    const bundleItemCount = this.getMealPlanProductTotal();
    const bundleSellingPlanWeeks = this.getBundleSellingPlanWeeks();

    if (!bundleProduct?.id || !bundleProduct?.variant?.id) {
      throw new Error('Container bundle product is not configured.');
    }

    if (!bundleSellingPlanWeeks.length) {
      throw new Error('Container bundle product has no configured selling plan frequencies.');
    }

    // IMPORTANT: CURRENT CONTAINER PLANS COVER EVERY WEEK IN THE SUPPORTED RANGE. IF GAPS ARE ADDED, SNAP THE CALCULATED FREQUENCY TO AN AVAILABLE PLAN.
    if (frequencyWeeks === null || !bundleSellingPlanWeeks.includes(frequencyWeeks)) {
      const minimumWeeks = Math.min(...bundleSellingPlanWeeks);
      const maximumWeeks = Math.max(...bundleSellingPlanWeeks);

      throw new Error(`Delivery frequency must be between ${minimumWeeks} and ${maximumWeeks} weeks.`);
    }

    const bundleUid = generateBundleUid();
    const properties = this.getAwtomicBundleProperties({
      bundleProduct,
      bundleUid,
      bundleItemCount,
    });

    const mealItems = this.state.mealPlan.products
      .filter((product) => product.variantId && product.quantity > 0)
      .map((product) => {
        const variant = this.getConfiguredVariant(product.variantId);
        const sellingPlan = this.getSellingPlanForVariant(variant, frequencyWeeks);

        if (!variant) {
          throw new Error(`Missing variant config for ${product.title}.`);
        }

        if (!sellingPlan?.id) {
          throw new Error(`No ${normalizeWeeksLabel(frequencyWeeks)} selling plan found for ${product.title}.`);
        }

        return {
          id: String(product.variantId),
          quantity: product.quantity,
          properties: {
            ...properties,
            _subscriptionQuizSource: 'quiz',
            _variantId: getShopifyGid('ProductVariant', product.variantId),
          },
          selling_plan: String(sellingPlan.id),
        };
      });
    if (mealItems.reduce((total, item) => total + item.quantity, 0) < MIN_MEAL_PACK_COUNT) {
      throw new Error(`Select at least ${MIN_MEAL_PACK_COUNT} packs of food.`);
    }

    const treatItems = this.state.subscriptionTreats
      .filter((treat) => treat.variantId && treat.quantity > 0)
      .map((treat) => {
        const treatConfig = this.getSubscriptionTreatConfig(treat.variantId);
        const variant = treatConfig?.variant;
        const sellingPlan = this.getSellingPlanForVariant(variant, frequencyWeeks);
        const title = treatConfig?.title || treat.title || 'subscription treat';

        if (!variant) {
          throw new Error(`Missing variant config for ${title}.`);
        }

        if (!sellingPlan?.id) {
          throw new Error(`No ${normalizeWeeksLabel(frequencyWeeks)} selling plan found for ${title}.`);
        }

        return {
          id: String(treat.variantId),
          quantity: treat.quantity,
          properties: {
            ...properties,
            _subscriptionQuizSource: 'quiz',
            _variantId: getShopifyGid('ProductVariant', treat.variantId),
          },
          selling_plan: String(sellingPlan.id),
        };
      });
    const freeTreatItems = this.state.freeTreat?.variantId
      ? [{
        id: String(this.state.freeTreat.variantId),
        quantity: 1,
        properties: {
          _addedFromSubscriptionQuiz: 'true',
          _subscriptionQuizSource: 'quiz',
        },
      }]
      : [];
    const items = [...mealItems, ...treatItems, ...freeTreatItems];

    if (!items.length) {
      throw new Error('No meal products selected.');
    }

    return { items };
  }

  getAwtomicBundleProperties({ bundleProduct, bundleUid, bundleItemCount }) {
    const variantTitle = bundleProduct.variant?.title || '';
    const hasUsefulVariantTitle = variantTitle && variantTitle.toLowerCase() !== 'default title';
    const properties = {
      _bundleUid: bundleUid,
      _bundleHandle: bundleProduct.handle,
      _bundleType: AWTOMIC_BUNDLE_TYPE,
      _bundleLabel: hasUsefulVariantTitle ? variantTitle : `${bundleItemCount} items`,
      _bundleProductId: bundleProduct.gid || getShopifyGid('Product', bundleProduct.id),
      _bundleName: bundleProduct.title,
      _bundleBoxMinSize: toNumber(bundleProduct.boxMinSize, 1),
    };

    return properties;
  }

  getBundleSellingPlanWeeks() {
    const allocations = this.bundleProductConfig?.variant?.sellingPlanAllocations || [];
    const frequencies = allocations
      .map((allocation) => getSellingPlanWeeks(allocation.sellingPlan))
      .filter((weeks) => weeks !== null && weeks > 0);

    return [...new Set(frequencies)].sort((firstWeeks, secondWeeks) => firstWeeks - secondWeeks);
  }

  getConfiguredVariant(variantId) {
    const numericVariantId = toNumber(variantId, null);
    const productGroups = Object.values(this.productsByMealType || {});

    for (const products of productGroups) {
      if (!Array.isArray(products)) continue;

      for (const product of products) {
        const variant = product.variants?.find((item) => toNumber(item.id, null) === numericVariantId);

        if (variant) {
          return variant;
        }
      }
    }

    return null;
  }

  getSellingPlanForVariant(variant, frequencyWeeks) {
    return this.getSellingPlanAllocationForVariant(variant, frequencyWeeks)?.sellingPlan || null;
  }

  getSellingPlanAllocationForVariant(variant, frequencyWeeks) {
    const allocations = variant?.sellingPlanAllocations || [];

    return allocations.find((allocation) => getSellingPlanWeeks(allocation.sellingPlan) === frequencyWeeks) || null;
  }

  getSubscriptionTreatConfig(variantId) {
    const numericVariantId = toNumber(variantId, null);

    return this.subscriptionTreatsConfig.find((treat) => (
      toNumber(treat.variant?.id, null) === numericVariantId
    )) || null;
  }
}

class IntroForm extends HTMLElement {
  connectedCallback() {
    this.nameInput = this.querySelector('.js-pet-name');
    this.breedInput = this.querySelector('.js-pet-breed');
    this.unknownInput = this.querySelector('.js-pet-breed-unknown');
    this.submitButton = this.querySelector('.js-quiz-intro-submit');
    this.errorElements = {
      name: this.querySelector('.js-pet-name-error'),
      breed: this.querySelector('.js-pet-breed-error'),
    };

    this.nameInput?.addEventListener('input', this);
    this.nameInput?.addEventListener('blur', this);
    this.breedInput?.addEventListener('input', this);
    this.breedInput?.addEventListener('blur', this);
    this.unknownInput?.addEventListener('change', this);
    this.submitButton?.addEventListener('click', this);
    this.enableSubmit();
  }

  disconnectedCallback() {
    this.nameInput?.removeEventListener('input', this);
    this.nameInput?.removeEventListener('blur', this);
    this.breedInput?.removeEventListener('input', this);
    this.breedInput?.removeEventListener('blur', this);
    this.unknownInput?.removeEventListener('change', this);
    this.submitButton?.removeEventListener('click', this);
  }

  handleEvent(event) {
    if (event.currentTarget === this.submitButton) {
      this.submit();
      return;
    }

    if (event.currentTarget === this.nameInput) {
      this.clearError('name');
    }

    if (event.currentTarget === this.breedInput || event.currentTarget === this.unknownInput) {
      this.syncBreedKnownState(event.currentTarget);
      this.clearError('breed');
    }
  }

  syncBreedKnownState(target) {
    if (!this.breedInput || !this.unknownInput) return;

    if (target === this.unknownInput && this.unknownInput.checked) {
      this.breedInput.value = '';
      return;
    }

    if (target === this.breedInput && this.breedInput.value.trim()) {
      this.unknownInput.checked = false;
    }
  }

  getValidationErrors() {
    const name = this.nameInput?.value.trim() || '';
    const breed = this.breedInput?.value.trim() || '';
    const isBreedUnknown = this.unknownInput?.checked;
    const errors = {};

    if (name.length < 3) {
      errors.name = true;
    }

    if (!isBreedUnknown && breed.length < 3) {
      errors.breed = true;
    }

    return errors;
  }

  isValid() {
    return Object.keys(this.getValidationErrors()).length === 0;
  }

  submit() {
    const errors = this.getValidationErrors();

    if (Object.keys(errors).length) {
      this.showErrors(errors);
      return;
    }

    document.dispatchEvent(
      new CustomEvent('subscription-quiz:intro-submit', {
        detail: {
          petName: this.nameInput?.value.trim() || '',
          petBreed: this.breedInput?.value.trim() || '',
          petBreedUnknown: Boolean(this.unknownInput?.checked),
        },
      }),
    );

    this.closest('modal-dialog')?.hide();
  }

  enableSubmit() {
    if (!this.submitButton) return;

    this.submitButton.disabled = false;
  }

  showErrors(errors) {
    Object.keys(this.errorElements).forEach((fieldName) => {
      if (errors[fieldName]) {
        this.showError(fieldName);
      } else {
        this.clearError(fieldName);
      }
    });
  }

  showError(fieldName) {
    const input = fieldName === 'name' ? this.nameInput : this.breedInput;
    const element = this.errorElements[fieldName];

    if (!element || !input) return;

    element.hidden = false;
    input.setAttribute('aria-invalid', 'true');
  }

  clearError(fieldName) {
    const input = fieldName === 'name' ? this.nameInput : this.breedInput;
    const element = this.errorElements[fieldName];

    if (!element || !input) return;

    element.hidden = true;
    input.removeAttribute('aria-invalid');
  }
}

class QuizIntro extends SubscriptionQuizStep {}

class QuizQuestionnaire extends SubscriptionQuizStep {
  connectedCallback() {
    super.connectedCallback();

    this.submitButton = this.querySelector('.sq-questionnaire__submit');
    this.fields = {
      ageYears: this.querySelector('.js-pet-age-years'),
      ageMonths: this.querySelector('.js-pet-age-months'),
      weight: this.querySelector('.js-pet-weight'),
      bodyShape: this.querySelectorAll('input[name="pet_body_shape"]'),
      activity: this.querySelectorAll('input[name="pet_activity"]'),
      ownerName: this.querySelector('.js-owner-name'),
      ownerEmail: this.querySelector('.js-owner-email'),
    };
    this.errorElements = {
      age: this.querySelector('.js-pet-age-error'),
      weight: this.querySelector('.js-pet-weight-error'),
      bodyShape: this.querySelector('.js-pet-body-shape-error'),
      activity: this.querySelector('.js-pet-activity-error'),
      ownerName: this.querySelector('.js-owner-name-error'),
      ownerEmail: this.querySelector('.js-owner-email-error'),
    };
    this.validationFields = [this.fields.ageYears, this.fields.ageMonths, this.fields.weight, this.fields.ownerName, this.fields.ownerEmail, ...this.fields.bodyShape, ...this.fields.activity].filter(
      Boolean,
    );

    this.submitButton?.addEventListener('click', this);
    this.validationFields.forEach((field) => {
      field.addEventListener('input', this);
      field.addEventListener('change', this);
      field.addEventListener('blur', this);
    });
  }

  disconnectedCallback() {
    this.submitButton?.removeEventListener('click', this);
    this.validationFields?.forEach((field) => {
      field.removeEventListener('input', this);
      field.removeEventListener('change', this);
      field.removeEventListener('blur', this);
    });
  }

  handleEvent(event) {
    if (event.currentTarget === this.submitButton) {
      this.submit();
      return;
    }

    this.clearError(this.getErrorNameByField(event.currentTarget));
  }

  submit() {
    const errors = this.getValidationErrors();

    if (Object.keys(errors).length) {
      this.showErrors(errors);
      const firstInvalidField = this.validationFields.find((field) => errors[this.getErrorNameByField(field)]);

      if (firstInvalidField) {
        firstInvalidField.focus({ preventScroll: true });
        const scrollTarget = firstInvalidField.closest('label') || firstInvalidField;
        scrollTarget.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          block: 'center',
        });
      }

      return;
    }

    this.parentQuiz?.submitQuestionnaire(this.getValues());
  }

  getValidationErrors() {
    const errors = {};
    const ageYears = toNumber(this.fields.ageYears?.value);
    const ageMonths = toNumber(this.fields.ageMonths?.value);
    const weight = toNumber(this.fields.weight?.value);
    // About You temporarily disabled; restore with the markup in sq-questionnaire.liquid.
    // const ownerName = this.fields.ownerName?.value.trim() || '';
    // const ownerEmail = this.fields.ownerEmail?.value.trim() || '';

    if (!ageYears && !ageMonths) {
      errors.age = true;
    }

    if (this.parentQuiz?.petType === 'cat' && getAgeInMonths({ ageYears, ageMonths }) < 2) {
      errors.age = true;

      if (this.errorElements.age) {
        this.errorElements.age.textContent = 'Kittens must be at least 8 weeks old (2 months) to use this calculator.';
      }
    }

    if (weight <= 0) {
      errors.weight = true;
    }

    if (!this.getCheckedValue(this.fields.bodyShape)) {
      errors.bodyShape = true;
    }

    if (!this.getCheckedValue(this.fields.activity)) {
      errors.activity = true;
    }

    // About You validation temporarily disabled.
    // if (!ownerName) {
    //   errors.ownerName = true;
    // }
    //
    // if (!ownerEmail || !this.fields.ownerEmail?.validity.valid) {
    //   errors.ownerEmail = true;
    // }

    return errors;
  }

  getCheckedValue(fields) {
    return Array.from(fields || []).find((field) => field.checked)?.value || '';
  }

  getErrorNameByField(field) {
    if (field === this.fields.ageYears || field === this.fields.ageMonths) return 'age';
    if (field === this.fields.weight) return 'weight';
    if (field === this.fields.ownerName) return 'ownerName';
    if (field === this.fields.ownerEmail) return 'ownerEmail';
    if (field?.name === 'pet_body_shape') return 'bodyShape';
    if (field?.name === 'pet_activity') return 'activity';

    return '';
  }

  showErrors(errors) {
    Object.keys(this.errorElements).forEach((fieldName) => {
      if (errors[fieldName]) {
        this.showError(fieldName);
      } else {
        this.clearError(fieldName);
      }
    });
  }

  showError(fieldName) {
    const element = this.errorElements[fieldName];

    if (!element) return;

    element.hidden = false;
    this.setFieldInvalid(fieldName, true);
  }

  clearError(fieldName) {
    const element = this.errorElements[fieldName];

    if (!element) return;

    element.hidden = true;
    this.setFieldInvalid(fieldName, false);
  }

  setFieldInvalid(fieldName, isInvalid) {
    const fieldsByError = {
      age: [this.fields.ageYears, this.fields.ageMonths],
      weight: [this.fields.weight],
      bodyShape: this.fields.bodyShape,
      activity: this.fields.activity,
      ownerName: [this.fields.ownerName],
      ownerEmail: [this.fields.ownerEmail],
    };

    Array.from(fieldsByError[fieldName] || []).forEach((field) => {
      if (!field) return;

      if (isInvalid) {
        field.setAttribute('aria-invalid', 'true');
      } else {
        field.removeAttribute('aria-invalid');
      }
    });
  }

  getValues() {
    const values = {
      pet: {},
      owner: {},
    };

    this.querySelectorAll('input[name], select[name], textarea[name]').forEach((field) => {
      const group = field.name.startsWith('owner_') || field.name === 'marketing_consent' ? 'owner' : 'pet';
      const key = this.getStateKey(field.name, group);

      if (field.type === 'radio') {
        if (field.checked) {
          values[group][key] = field.value;
        }

        return;
      }

      if (field.type === 'checkbox') {
        values[group][key] = field.checked;
        return;
      }

      values[group][key] = field.value.trim();
    });

    return values;
  }

  getStateKey(name, group) {
    const prefix = group === 'owner' ? 'owner_' : 'pet_';
    const normalizedName = name === 'marketing_consent' ? name : name.replace(prefix, '');

    return normalizedName.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }
}

class QuizPacks extends SubscriptionQuizStep {
  connectedCallback() {
    super.connectedCallback();

    this.submitButton = this.querySelector('.js-packs-submit');
    this.stepElement = this.querySelector('[data-packs-step]');
    this.contentElement = this.querySelector('[data-packs-content]');
    this.loadingElement = this.querySelector('[data-packs-loading]');
    this.loadingTimeoutId = null;

    this.submitButton?.addEventListener('click', this);
  }

  disconnectedCallback() {
    this.submitButton?.removeEventListener('click', this);
    this.clearLoadingTimeout();
  }

  handleEvent(event) {
    if (event.currentTarget === this.submitButton) {
      this.submit();
    }
  }

  submit() {
    if (this.loadingTimeoutId !== null) return;

    const packCount = this.querySelector('.js-packs-input:checked')?.value || '';
    const shouldShowLoadingProgress = this.stepElement?.dataset.showLoadingProgress === 'true';

    if (!shouldShowLoadingProgress) {
      this.parentQuiz?.submitPacks(packCount);
      return;
    }

    this.setLoading(true);
    const delay = Math.max(0, toNumber(this.stepElement?.dataset.loadingProgressDelay));

    this.loadingTimeoutId = window.setTimeout(() => {
      this.loadingTimeoutId = null;
      this.setLoading(false);
      this.parentQuiz?.submitPacks(packCount);
    }, delay);
  }

  setLoading(isLoading) {
    if (this.contentElement) this.contentElement.hidden = isLoading;
    if (this.loadingElement) this.loadingElement.hidden = !isLoading;
    if (this.submitButton) this.submitButton.disabled = isLoading;
  }

  clearLoadingTimeout() {
    if (this.loadingTimeoutId === null) return;

    window.clearTimeout(this.loadingTimeoutId);
    this.loadingTimeoutId = null;
  }
}

class QuizMealType extends SubscriptionQuizStep {
  connectedCallback() {
    super.connectedCallback();

    this.inputs = this.querySelectorAll('[data-meal-type-input]');

    this.inputs.forEach((input) => {
      input.addEventListener('change', this);
    });
  }

  disconnectedCallback() {
    this.inputs.forEach((input) => {
      input.removeEventListener('change', this);
    });
  }

  handleEvent(event) {
    if (!event.target.checked) return;

    this.parentQuiz?.selectMealType(event.target.value);
  }
}

class QuizSingleMealCollection extends SubscriptionQuizStep {}

class QuizFreeTreat extends SubscriptionQuizStep {
  connectedCallback() {
    super.connectedCallback();

    this.options = this.querySelectorAll('[data-free-treat-option]');
    this.submitButton = this.querySelector('[data-free-treat-submit]');

    this.options.forEach((option) => {
      option.addEventListener('click', this);
    });
    this.submitButton?.addEventListener('click', this);
  }

  disconnectedCallback() {
    this.options.forEach((option) => {
      option.removeEventListener('click', this);
    });
    this.submitButton?.removeEventListener('click', this);
  }

  handleEvent(event) {
    const option = event.currentTarget.closest?.('[data-free-treat-option]');

    if (option) {
      this.parentQuiz?.selectFreeTreat({
        productId: option.dataset.productId,
        variantId: option.dataset.variantId,
      });
      return;
    }

    if (event.currentTarget === this.submitButton) {
      this.parentQuiz?.submitFreeTreat();
    }
  }
}

if (!customElements.get('subscription-quiz-step')) {
  customElements.define('subscription-quiz-step', SubscriptionQuizStep);
}

if (!customElements.get('intro-form')) {
  customElements.define('intro-form', IntroForm);
}

if (!customElements.get('subscription-quiz')) {
  customElements.define('subscription-quiz', SubscriptionQuiz);
}

if (!customElements.get('quiz-intro')) {
  customElements.define('quiz-intro', QuizIntro);
}

if (!customElements.get('quiz-questionnaire')) {
  customElements.define('quiz-questionnaire', QuizQuestionnaire);
}

if (!customElements.get('quiz-packs')) {
  customElements.define('quiz-packs', QuizPacks);
}

if (!customElements.get('quiz-meal-type')) {
  customElements.define('quiz-meal-type', QuizMealType);
}

if (!customElements.get('quiz-single-meal-collection')) {
  customElements.define('quiz-single-meal-collection', QuizSingleMealCollection);
}

if (!customElements.get('quiz-free-treat')) {
  customElements.define('quiz-free-treat', QuizFreeTreat);
}

class QuizSubscriptionTreats extends SubscriptionQuizStep {}

if (!customElements.get('quiz-subscription-treats')) {
  customElements.define('quiz-subscription-treats', QuizSubscriptionTreats);
}
